'use client';

import { c as _c } from "react/compiler-runtime";
import { Button } from '@achra/ui/button';
import { cn } from '@achra/ui/lib/utils';
import { RadioGroup } from '@achra/ui/radio-group';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import type { ServiceOfferingFieldsFragment } from '@/modules/__generated__/graphql/switchboard-generated';
import { createStickyObserver, STICKY_HEADER_SM_BREAKPOINT } from '@/modules/service-purchase/lib/sticky-observer-utils';
import { useServicePurchaseActions } from '@/modules/service-purchase/providers/service-purchase-store-provider';
import { PlanSelectorItem } from '../plan-selector';
interface HeaderCatalogPlanProps {
  selectedPlan?: string;
  handlePlanChange: (plan: string) => void;
  readOnly?: boolean;
  mobilePlanIndex: number;
  onPrevPlan: () => void;
  onNextPlan: () => void;
  servicesData: ServiceOfferingFieldsFragment;
}
function HeaderCatalogPlan(t0) {
  const $ = _c(49);
  const {
    selectedPlan,
    handlePlanChange,
    readOnly: t1,
    mobilePlanIndex,
    onPrevPlan,
    onNextPlan,
    servicesData
  } = t0;
  const readOnly = t1 === undefined ? false : t1;
  const {
    setHoveredTier
  } = useServicePurchaseActions();
  const tiers = servicesData.tiers;
  const numTiers = tiers.length;
  const currentMobileTier = tiers[mobilePlanIndex];
  const sentinelRef = useRef(null);
  const [isStuck, setIsStuck] = useState(false);
  let t2;
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      const sentinel = sentinelRef.current;
      if (!sentinel) {
        return;
      }
      let observer = createStickyObserver(sentinel, setIsStuck);
      const mql = window.matchMedia(STICKY_HEADER_SM_BREAKPOINT);
      const handleBreakpointChange = () => {
        observer.disconnect();
        observer = createStickyObserver(sentinel, setIsStuck);
      };
      mql.addEventListener("change", handleBreakpointChange);
      return () => {
        mql.removeEventListener("change", handleBreakpointChange);
        observer.disconnect();
      };
    };
    t3 = [];
    $[0] = t2;
    $[1] = t3;
  } else {
    t2 = $[0];
    t3 = $[1];
  }
  useEffect(t2, t3);
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div ref={sentinelRef} className="h-0 w-full" aria-hidden="true" />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = `4fr repeat(${numTiers}, minmax(144px, 1fr))`;
  const t6 = `4fr repeat(${numTiers}, minmax(220px, 1fr))`;
  let t7;
  if ($[3] !== t5 || $[4] !== t6) {
    t7 = {
      "--grid-cols-lg": t5,
      "--grid-cols-xl": t6
    };
    $[3] = t5;
    $[4] = t6;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const t8 = t7 as React.CSSProperties;
  const t9 = isStuck ? "border-primary/30 shadow-primary/20 rounded-xl border" : "rounded-xl border shadow-[0_-1px_2px_0_rgb(0_0_0/0.05),-1px_0_2px_0_rgb(0_0_0/0.05),1px_0_2px_0_rgb(0_0_0/0.05)]";
  let t10;
  if ($[6] !== t9) {
    t10 = cn("bg-card sticky top-18 z-20 min-h-21 w-full items-center overflow-clip sm:top-27.5", "grid grid-cols-2 lg:grid-cols-(--grid-cols-lg) xl:grid-cols-(--grid-cols-xl)", "transition-all duration-500", t9);
    $[6] = t9;
    $[7] = t10;
  } else {
    t10 = $[7];
  }
  let t11;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <div className="flex h-full items-center px-4 lg:px-6" />;
    $[8] = t11;
  } else {
    t11 = $[8];
  }
  const t12 = currentMobileTier.mostPopular ? "bg-primary/10 border-t-primary border-t-2" : "bg-primary/10";
  let t13;
  if ($[9] !== t12) {
    t13 = cn("flex h-full w-full items-center justify-between px-1 py-2 lg:hidden", t12);
    $[9] = t12;
    $[10] = t13;
  } else {
    t13 = $[10];
  }
  let t14;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronLeft className="size-5" />;
    $[11] = t14;
  } else {
    t14 = $[11];
  }
  let t15;
  if ($[12] !== onPrevPlan) {
    t15 = <Button variant="ghost" size="icon" onClick={onPrevPlan} className="size-8">{t14}</Button>;
    $[12] = onPrevPlan;
    $[13] = t15;
  } else {
    t15 = $[13];
  }
  const t16 = selectedPlan === currentMobileTier.id;
  let t17;
  if ($[14] !== currentMobileTier || $[15] !== t16) {
    t17 = <PlanSelectorItem tier={currentMobileTier} isSelected={t16} />;
    $[14] = currentMobileTier;
    $[15] = t16;
    $[16] = t17;
  } else {
    t17 = $[16];
  }
  let t18;
  if ($[17] !== handlePlanChange || $[18] !== readOnly || $[19] !== selectedPlan || $[20] !== t17) {
    t18 = <div className="flex flex-col items-center justify-center"><RadioGroup value={selectedPlan} onValueChange={handlePlanChange} disabled={readOnly}>{t17}</RadioGroup></div>;
    $[17] = handlePlanChange;
    $[18] = readOnly;
    $[19] = selectedPlan;
    $[20] = t17;
    $[21] = t18;
  } else {
    t18 = $[21];
  }
  let t19;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <ChevronRight className="size-5" />;
    $[22] = t19;
  } else {
    t19 = $[22];
  }
  let t20;
  if ($[23] !== onNextPlan) {
    t20 = <Button variant="ghost" size="icon" onClick={onNextPlan} className="size-8">{t19}</Button>;
    $[23] = onNextPlan;
    $[24] = t20;
  } else {
    t20 = $[24];
  }
  let t21;
  if ($[25] !== t13 || $[26] !== t15 || $[27] !== t18 || $[28] !== t20) {
    t21 = <div className={t13}>{t15}{t18}{t20}</div>;
    $[25] = t13;
    $[26] = t15;
    $[27] = t18;
    $[28] = t20;
    $[29] = t21;
  } else {
    t21 = $[29];
  }
  let t22;
  if ($[30] !== handlePlanChange) {
    t22 = val => {
      handlePlanChange(val);
    };
    $[30] = handlePlanChange;
    $[31] = t22;
  } else {
    t22 = $[31];
  }
  let t23;
  if ($[32] !== selectedPlan || $[33] !== setHoveredTier || $[34] !== tiers) {
    let t24;
    if ($[36] !== selectedPlan || $[37] !== setHoveredTier) {
      t24 = tier => {
        const isActive = selectedPlan === tier.id;
        return <div key={tier.id} className={cn("relative flex h-full min-w-0 items-center justify-center py-2 transition-colors", isActive && "bg-primary/10", !isActive && "hover:bg-accent/80", tier.mostPopular && "border-t-primary border-t-2")} onMouseEnter={() => {
          setHoveredTier(tier.id);
        }} onMouseLeave={() => {
          setHoveredTier(null);
        }}><PlanSelectorItem tier={tier} isSelected={isActive} /></div>;
      };
      $[36] = selectedPlan;
      $[37] = setHoveredTier;
      $[38] = t24;
    } else {
      t24 = $[38];
    }
    t23 = tiers.map(t24);
    $[32] = selectedPlan;
    $[33] = setHoveredTier;
    $[34] = tiers;
    $[35] = t23;
  } else {
    t23 = $[35];
  }
  let t24;
  if ($[39] !== readOnly || $[40] !== selectedPlan || $[41] !== t22 || $[42] !== t23) {
    t24 = <RadioGroup value={selectedPlan} onValueChange={t22} disabled={readOnly} className="hidden lg:contents">{t23}</RadioGroup>;
    $[39] = readOnly;
    $[40] = selectedPlan;
    $[41] = t22;
    $[42] = t23;
    $[43] = t24;
  } else {
    t24 = $[43];
  }
  let t25;
  if ($[44] !== t10 || $[45] !== t21 || $[46] !== t24 || $[47] !== t8) {
    t25 = <>{t4}<div style={t8} className={t10}>{t11}{t21}{t24}</div></>;
    $[44] = t10;
    $[45] = t21;
    $[46] = t24;
    $[47] = t8;
    $[48] = t25;
  } else {
    t25 = $[48];
  }
  return t25;
}
export { HeaderCatalogPlan };